import { CSSProperties, ElementType } from "react";
import {
  Button as MuiButton,
  CircularProgress,
  colors,
  makeStyles,
} from "@material-ui/core";
import { ButtonProps as MuiButtonProps } from "@material-ui/core/Button";

import theme, { Theme } from "theme";

const baseButtonStyles: CSSProperties = {
  borderRadius: theme.shape.borderRadius,
  fontWeight: theme.typography.fontWeightMedium,
  textTransform: "none",
};

const buttonTypes = {
  blue: {
    contained: {
      color: theme.palette.background.default,
      borderColor: theme.custom.palette.blue.main,
      backgroundColor: theme.custom.palette.blue.main,
      "&:hover": {
        color: theme.palette.background.default,
        borderColor: theme.custom.palette.blue.dark,
        backgroundColor: theme.custom.palette.blue.dark,
      },
      "&:disabled": {
        color: theme.palette.background.default,
        backgroundColor: `${theme.custom.palette.blue.main} !important`,
        opacity: ".5",
      },
    },
    text: {
      color: theme.custom.palette.blue.main,
      backgroundColor: theme.palette.background.default,
      "&:hover": {
        backgroundColor: theme.custom.palette.blue.lightest,
      },
    },
  },
  green: {
    contained: {
      color: theme.palette.background.default,
      borderColor: theme.custom.palette.green.main,
      backgroundColor: theme.custom.palette.green.main,
      "&:hover": {
        color: theme.palette.background.default,
        borderColor: theme.custom.palette.green.dark,
        backgroundColor: theme.custom.palette.green.dark,
      },
      "&:disabled": {
        color: theme.palette.background.default,
        backgroundColor: `${theme.custom.palette.green.main} !important`,
        opacity: ".5",
      },
    },
    outlined: {
      color: theme.custom.palette.green.main,
      borderColor: theme.custom.palette.green.main,
      backgroundColor: "transparent",
      borderWidth: "2px",
      "&:hover": {
        color: theme.custom.palette.green.main,
        backgroundColor: theme.custom.palette.green.light,
      },
      "&:disabled": {
        color: theme.custom.palette.green.main,
        borderColor: theme.custom.palette.green.main,
        borderWidth: "2px",
        opacity: ".5",
      },
    },
  },
  gray: {
    contained: {
      color: theme.custom.palette.black.main,
      borderColor: theme.custom.palette.gray.main,
      backgroundColor: theme.custom.palette.gray.main,
      "&:hover": {
        color: theme.palette.background.default,
        borderColor: theme.custom.palette.gray.main,
        backgroundColor: theme.custom.palette.gray.dark,
      },
    },
    outlined: {
      "&:hover": {
        color: theme.custom.palette.black.main,
        borderColor: theme.custom.palette.gray.dark,
        backgroundColor: theme.custom.palette.gray.main,
      },
    },
  },
  purple: {
    contained: {
      color: theme.palette.background.default,
      borderColor: theme.custom.palette.purple.main,
      backgroundColor: theme.custom.palette.purple.main,
      "&:hover": {
        color: theme.palette.background.default,
        borderColor: theme.custom.palette.purple.dark,
        backgroundColor: theme.custom.palette.purple.dark,
      },
      "&:disabled": {
        backgroundColor: theme.custom.palette.purple.lighter,
        color: theme.palette.background.default,
      },
    },
    outlined: {
      color: theme.custom.palette.purple.main,
      backgroundColor: theme.palette.background.default,
      border: `2px solid ${theme.custom.palette.purple.main}`,
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      "&:hover": {
        color: theme.custom.palette.purple.main,
        backgroundColor: theme.custom.palette.purple.light,
        cursor: "pointer",
      },
    },
  },
  red: {
    contained: {
      color: theme.palette.getContrastText(colors.red[500]),
      backgroundColor: colors.red[500],
      "&:hover": {
        backgroundColor: colors.red[700],
      },
    },
    outlined: {
      color: colors.red[500],
      borderColor: colors.red[500],
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: colors.red[50],
      },
    },
    text: {
      color: colors.red[500],
      backgroundColor: theme.palette.getContrastText(colors.red[500]),
      "&:hover": {
        backgroundColor: colors.red[50],
      },
    },
  },
  softRed: {
    contained: {
      color: theme.palette.background.default,
      borderColor: theme.custom.palette.red.main,
      backgroundColor: theme.custom.palette.red.main,
      "&:hover": {
        color: theme.palette.background.default,
        borderColor: theme.custom.palette.red.dark,
        backgroundColor: theme.custom.palette.red.dark,
      },
      "&:disabled": {
        color: theme.palette.background.default,
        backgroundColor: `${theme.custom.palette.red.main} !important`,
        opacity: ".5",
      },
    },
  },
  white: {
    text: {
      color: theme.custom.palette.softWhite.main,
      backgroundColor: "transparent",
      "&:hover": {
        color: theme.custom.palette.softWhite.main,
        backgroundColor: theme.custom.palette.softWhite.light,
      },
    },
    outlined: {
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.custom.palette.black.main}`,
      "&:hover": {
        backgroundColor: theme.custom.palette.softWhite.dark,
        boxShadow: theme.shadows[3],
      },
    },
  },
  yellow: {
    contained: {
      color: theme.palette.background.default,
      borderColor: theme.custom.palette.yellow.main,
      backgroundColor: theme.custom.palette.yellow.main,
      "&:hover": {
        color: theme.palette.background.default,
        borderColor: theme.custom.palette.yellow.dark,
        backgroundColor: theme.custom.palette.yellow.dark,
      },
      "&:disabled": {
        color: theme.palette.background.default,
        backgroundColor: `${theme.custom.palette.yellow.main} !important`,
        opacity: ".5",
      },
    },
  },
};

type ButtonTypeStylesProps = {
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  "&:hover"?: {
    color?: string;
    borderColor?: string;
    backgroundColor?: string;
  };
};

type ButtonTypeProps = {
  contained?: ButtonTypeStylesProps;
  outlined?: ButtonTypeStylesProps;
  disabled?: ButtonTypeStylesProps;
  text?: ButtonTypeStylesProps;
};

type ButtonStyleProps = ButtonTypeProps & {
  isLoading?: boolean;
};

const useStyles = makeStyles<Theme, ButtonStyleProps>({
  root: { ...baseButtonStyles },
  contained: props => ({ ...props?.contained }),
  outlined: props => ({ ...props?.outlined }),
  disabled: props => ({ ...props?.disabled }),
  text: props => ({ ...props?.text }),
  loadingWrapper: props => ({
    display: "inline-flex",
    alignItems: "center",
    marginRight: props.isLoading ? theme.spacing(1) : 0,
    // Keep DOM structure stable by always rendering but hiding when not loading
    // This prevents crashes when browser translation modifies the DOM
    visibility: props.isLoading ? "visible" : "hidden",
    width: props.isLoading ? "auto" : 0,
    overflow: "hidden",
  }),
});

interface ButtonProps {
  customColor?: keyof typeof buttonTypes;
  isLoading?: boolean;
}

function Button<C extends ElementType>(
  props: MuiButtonProps<C, { component?: C }> & ButtonProps,
) {
  const { customColor, isLoading, ...restProps } = props;

  const classes = useStyles({
    ...(customColor ? buttonTypes[customColor] : buttonTypes["purple"]),
    isLoading,
  });

  return (
    <MuiButton
      {...restProps}
      classes={{
        root: classes.root,
        contained: classes.contained,
        outlined: classes.outlined,
        text: classes.text,
      }}
    >
      <span className={classes.loadingWrapper}>
        <CircularProgress size={15} style={{ color: "inherit" }} />
      </span>
      {props.children}
    </MuiButton>
  );
}
export default Button;
